/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import {Typography} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {
  getAnySearchCriteria,
  getExcludeDevicesQuery,
  getNewSearchCriteria,
  hasAnySearch,
  hasMaxSearch,
  isNestedSort
} from '@ion/applications/src/pages/devices/ModularSearchUtils';
import {ExportContext} from '@ion/applications/src/types/redux';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {emailExportLargeEntities} from '@ion/core/src/services/Connect';
import {getUserTimeZone} from '@ion/core/src/timezone/timezone';
import {
  CTCEntity,
  DownloadFileTypes,
  GenericObject
} from '@ion/core/src/types/entities';
import {
  APIGatewayMemberSearch,
  ExportSearch,
  HierLevel,
  JobSearchEngineExportSearch,
  JobsTaskExportSearch
} from '@ion/core/src/types/services';
import {isEqual} from '@ion/core/src/utils/StringUtil';
import {isValidEmail} from '@ion/core/src/utils/Validators';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {SearchFilters} from '@ion/features/src/search/types';
import {useTaskEmailExportOperation} from '@ion/query/src/hooks/useTaskEmailExportOperation';
import {ChipTextField} from '../components/textfield/ChipTextField';
import {CancelButton} from '../editors/controls/CancelButton';
import {SaveButton} from '../editors/controls/SaveButton';
import styles, {getClasses} from '../editors/editorStyles';
import {useExtendStyles} from '../hooks/useExtendStyles';
import {useFormSave} from '../hooks/useFormSave';
import {useFormState, validate} from '../hooks/useFormState';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {dlsColor} from '../theme/theme';
import {DLSTheme, IconSize} from '../types/types';
import {
  buildJobSearchEngineQueryString,
  buildJobsTaskMemberQueryString,
  buildMemberQueryString,
  buildQueryString,
  EmailExportFields,
  getCustomAPIFields,
  getCustomLogicFields,
  getDateFormatFields,
  getInitialState,
  getJobSearchCustomLogicFields,
  getJobSearchDateFormatFields,
  getSearchFilters,
  serialize
} from './EmailExportRequestUtils';

export type EmailExportRequestDialogProps = {
  exportCount: number;
  show: boolean;
  viewSubGroups?: boolean;
  searchFilters?: SearchFilters;
  accountId?: number;
  exportContext?: ExportContext;
  showSnackbar: Function;
  onClose: Function;
  getExportColumnDefinitions?: Function;
  search?: SearchFilters;
  sortOn?: GenericObject;
  excludedRows?: CTCEntity[];
  excludeInheritable?: boolean;
  taskId?: string;
  isTaskStatus?: boolean;
  isDeviceTask?: boolean;
  isJobSearchEngine?: boolean;
  isIndividualJob?: boolean;
  filters?: string[];
  nameFilter?: string;
  dateRange?: string;
  format?: DownloadFileTypes;
  additionalProperties?: string[];
  jobId?: number;
  taskIndex?: number;
  includeInheritableGroup?: boolean;
  hasInheritableGroups?: boolean;
  isVbuFwEnabled?: boolean;
}

// defines the styles used by Email Export dialog ...
const styleOverrides = (theme: DLSTheme) => ({
  formPaper : {
    backgroundColor                : `${dlsColor('grey6')} !important`,
    maxWidth                       : '60%',
    minWidth                       : '60%',
    overflow                       : 'hidden',
    margin                         : 'auto',
    borderRadius                   : '10px',
    [theme.breakpoints.down('sm')] : {
      width     : '100%',
      maxHeight : '100% !important',
      margin    : '0px'
    }
  },
  paperScrollPaper : {
    maxHeight : 'calc(100% - 94px)',
    minHeight : 'calc(75% - 94px)'
  },
  scrollPaper : {
    flexDirection  : 'column',
    justifyContent : 'flex-end'
  },
  formDialogContent : {
    margin    : '25px',
    overflowY : 'scroll',
    overflowX : 'hidden'
  },
  closeButton : {
    top   : '26px',
    width : '3%'
  },
  titleContainer : {
    width : '70%'
  },
  formDialogTitle : {
    borderBottom : `solid 1px ${dlsColor('grey4')}`,
    paddingTop   : '20px'
  },
  formTemplate : {
    width         : '100%',
    margin        : '20px',
    display       : 'flex',
    flexDirection : 'column'
  },
  gridFormItem : {
    display       : 'flex',
    flexDirection : 'row',
    '& div'       : {
      marginBottom : '5px'
    }
  },
  formTextField : {
    width : '295px'
  }
});

/**
 * @EmailExportRequestDialog
 *
 * The Email Export Request Prompt displays the list of emails to send an email with export link
 *
 * ```typescript
 *
 * import {EmailExportRequestDialog} from '@ion/dls/src/export/EmailExportRequestDialog';
 *
 * <EmailExportRequestDialog exportCount={gridExportCount}
 *   show={isEmailExportRequest}
 *   exportContext={exportContext}
 *   onClose={onCloseEmailExportDialog} />
 * ```
 *
 * @param {number} exportCount count of selected items to be exported
 * @param {ExportContext} exportContext export context type that is being exported
 * @param {boolean} show display the dialog used during export
 * @param {Function} onClose callback invoked when the user clicks cancel.
 */
export const EmailExportRequestDialog = ({
  exportCount,
  show,
  search,
  sortOn,
  viewSubGroups,
  accountId,
  searchFilters,
  exportContext,
  showSnackbar,
  onClose,
  excludedRows,
  getExportColumnDefinitions,
  taskId,
  jobId,
  taskIndex,
  dateRange,
  nameFilter,
  filters = [],
  additionalProperties = [],
  format = DownloadFileTypes.CSV,
  hasInheritableGroups = false,
  includeInheritableGroup = false,
  excludeInheritable = false,
  isIndividualJob = false,
  isJobSearchEngine = false,
  isTaskStatus = false,
  isDeviceTask = false,
  isVbuFwEnabled = false
}: EmailExportRequestDialogProps) => {
  const maxInputEmailLength                 = 7;
  const isDeviceFiles                       = isEqual(exportContext, ExportContext.deviceFiles);
  const entity                              = isDeviceFiles ? translate(`common:modularsearch.ariaLabels.${exportContext}`) : exportContext;
  const editors                             = translate('common:editors.emailAddress', {returnObjects: true});
  const exportPath                          = translate('common:export.emailExporting', {returnObjects: true, entity, format});
  const dialogStyles                        = useExtendStyles(styles, styleOverrides);
  const classes                             = dialogStyles();
  const [disable, setDisable]               = useState<boolean>(true);
  const [loading, setLoading]               = useState<boolean>(false);
  const [maxLengthError, setMaxLengthError] = useState<boolean>(false);
  const [formState, setFormState]           = useFormState(getInitialState());
  const {process}                           = useTaskEmailExportOperation();

  const [
    searchCriteria, setSearchCriteria
  ] = useState<ExportSearch | APIGatewayMemberSearch | JobsTaskExportSearch | JobSearchEngineExportSearch>();

  useEffect(() => {
    if (isDeviceTask) {
      setSearchCriteria(buildJobsTaskMemberQueryString(accountId, jobId, taskIndex));
    } else if (isJobSearchEngine) {
      setSearchCriteria(buildJobSearchEngineQueryString({
        includeSubaccounts : isIndividualJob ? true : viewSubGroups,
        sort               : sortOn,
        accountId,
        ...(isEmpty(nameFilter) && {queryString: searchFilters}),
        ...(!isEmpty(nameFilter) && {
          query : getSearchFilters({
            filters,
            dateRange,
            nameFilter,
            query : searchFilters
          })
        }),
        ...(!isIndividualJob && {additionalProperties})
      }));
    } else if (search?.query) {
      setSearchCriteria(buildQueryString(viewSubGroups, searchFilters, accountId));
    } else {
      setSearchCriteria(buildMemberQueryString(sortOn, search));
    }
  }, [
    sortOn,
    filters,
    accountId,
    dateRange,
    nameFilter,
    isDeviceTask,
    searchFilters,
    viewSubGroups,
    isIndividualJob,
    isJobSearchEngine,
    additionalProperties
  ]);

  const onCloseDialog = useCallback(() => {
    onClose?.();
  }, []);

  const getGroupColumn = (columns): boolean => {
    return Object.keys(columns).indexOf('groups') !== -1;
  };

  const emailExport = useCallback(async(data) => {
    let isAnySearch = false;
    let isMaxSearch = false;

    setLoading(true);

    const {columns, sortOn} = getExportColumnDefinitions();
    const hasGroupColumn    = getGroupColumn(columns);

    if (searchCriteria.smartSearch?.search?.queryString) {
      searchCriteria.smartSearch.search.sort = 'id';
    } if (!isNil(searchFilters)) {
      isAnySearch = hasAnySearch(searchFilters);
      isMaxSearch = hasMaxSearch(searchFilters);
    }

    const timeZone: string = getUserTimeZone();
    const excludedIds      = excludedRows?.map(({id}) => id);
    const response         = await emailExportLargeEntities({
      entityType        : isDeviceFiles ? ExportContext.filemeta : exportContext,
      emailRecipients   : data[EmailExportFields.EmailAddress],
      exportColumns     : columns,
      customLogicFields : getCustomLogicFields({
        columns,
        isDeviceTask,
        isVbuFwEnabled : !isVbuFwEnabled
      }),
      dateFormatFields : getDateFormatFields(columns),
      customAPIFields  : getCustomAPIFields(columns, accountId),
      searchCriteria   : isMaxSearch ? {
        smartSearch : {
          search : getNewSearchCriteria({
            search             : searchFilters,
            account            : accountId,
            sort               : 'id',
            excluded           : true,
            includeDefaultSort : true,
            viewSubGroups,
            excludedIds,
            isVbuFwEnabled
          })?.sortOn
        }
      } : (isAnySearch || isNestedSort(sortOn?.[0]))
        ? getAnySearchCriteria(search, viewSubGroups, false) : excludedIds?.length
          ? getExcludeDevicesQuery({
            search : searchFilters,
            excludedIds,
            accountId,
            viewSubGroups,
            isVbuFwEnabled
          }) : searchCriteria,
      timeZone,
      ...(viewSubGroups && {hierLevel: HierLevel.ALL}),
      ...(excludeInheritable && {exclude: 'inheritable'}),
      ...(isDeviceTask && {action: 'jobsTaskDevices'}),
      ...(hasInheritableGroups && hasGroupColumn && {
        viewInheritableGroup      : includeInheritableGroup,
        groupInheritableAccountId : accountId
      })
    });

    if (!response?.error) {
      showSnackbar?.(exportPath.completed);
    } else {
      showSnackbar?.(exportPath.error);
    }

    setLoading(false);
    onCloseDialog();
  }, [
    excludeInheritable,
    exportContext,
    search,
    searchCriteria,
    viewSubGroups,
    hasInheritableGroups,
    includeInheritableGroup
  ]);

  const taskStatusEmailExport = useCallback(async(data) => {
    const {columns}        = getExportColumnDefinitions() || {};
    const timeZone: string = getUserTimeZone();
    setLoading(true);
    process?.({
      taskId,
      timeZone,
      emailRecipients   : data[EmailExportFields.EmailAddress],
      exportColumns     : columns,
      customLogicFields : getCustomLogicFields({columns}),
      dateFormatFields  : getDateFormatFields(columns)
    }, {
      onSuccess : () => {
        showSnackbar?.(exportPath.completed);
        setLoading(false);
        onCloseDialog();
      },
      onError : () => {
        showSnackbar?.(exportPath.error);
        setLoading(false);
      }
    });
  }, []);

  const jobSearchEngineEmailExport = useCallback(async(data) => {
    setLoading(true);

    const {columns, individualColumn} = getExportColumnDefinitions() || {};
    const timeZone: string            = getUserTimeZone();
    try {
      const response = await emailExportLargeEntities({
        entityType        : exportContext,
        emailRecipients   : data[EmailExportFields.EmailAddress],
        exportColumns     : isIndividualJob ? individualColumn : columns,
        customLogicFields : getJobSearchCustomLogicFields(columns, isIndividualJob),
        dateFormatFields  : getJobSearchDateFormatFields(columns, isIndividualJob),
        ...(isIndividualJob && {
          exportJobSummary : true
        }),
        searchCriteria,
        timeZone
      });
      if (!response?.error) {
        showSnackbar?.(exportPath.completed);
      } else {
        showSnackbar?.(exportPath.error);
      }
    } catch (err) {
      setLoading(false);
      error('Failed to export:', err);
    }

    setLoading(false);
    onCloseDialog();
  }, [exportContext, isIndividualJob, searchCriteria]);

  const handleEmailExport = useCallback((data) => {
    if (isTaskStatus) {
      return taskStatusEmailExport(data);
    }
    if (isJobSearchEngine) {
      return jobSearchEngineEmailExport(data);
    }
    return emailExport(data);
  }, [
    emailExport,
    isTaskStatus,
    isJobSearchEngine,
    taskStatusEmailExport,
    jobSearchEngineEmailExport
  ]);

  const onSubmit = useFormSave({
    onSave : handleEmailExport,
    serialize,
    formState,
    setFormState
  });

  const validateEmail = useCallback((value) => {
    const items = [];
    value?.forEach(item => {
      const error = item && !validate(item, isValidEmail);
      items.push(error);
    });
    return items;
  }, []);

  const handleFieldChange = useCallback((value) => {
    const name  = EmailExportFields.EmailAddress;
    const error =  validateEmail(value);
    setFormState({
      [name] : {
        name,
        value,
        required : false,
        error    : error?.some((item) => item === true)
      }
    });
  }, [formState]);

  const onUpdateInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    e.persist();
    const {name, value} = e?.target;
    const error = value && !validate(value, isValidEmail);
    if (!value) setMaxLengthError(false);
    setFormState({
      [name] : {
        ...formState[name],
        error
      }
    });
  }, [formState]);

  const onBeforeAdd = useCallback((value: string): boolean => {
    const validate = !formState[EmailExportFields.EmailAddress]?.value ||
    (formState[EmailExportFields.EmailAddress]?.value?.length < maxInputEmailLength);
    setMaxLengthError(value && !validate);
    return validate;
  }, [formState]);

  const handleDialogTitle = useCallback(() => {
    if (isIndividualJob) {
      return exportPath.title;
    }
    return `${exportCount} ${exportPath.title}`;
  }, [isIndividualJob, exportPath, exportCount]);

  const handleDialogContent = useCallback(() => {
    if (isDeviceFiles || isTaskStatus || isJobSearchEngine) {
      return exportPath.exportDescription;
    }
    return exportPath.description;
  }, [isDeviceFiles, isTaskStatus, isJobSearchEngine, exportPath]);

  useEffect(() => {
    const formStateEmail = formState[EmailExportFields.EmailAddress];
    setDisable(
      loading ||
      maxLengthError ||
      formStateEmail?.error ||
      !formStateEmail?.value?.length
    );
  }, [formState, loading, maxLengthError]);

  return (
    <>
      {show && (
        <Dialog open={show}
          classes={getClasses(classes)}>
          <DialogTitle classes={{
            root : classes.formDialogTitle
          }}>
            <div className={classes.formTitle}>
              <div onClick={onCloseDialog} className={classes.closeButton}>
                <MaterialExitCloseIcon size={IconSize.medium}
                  color={dlsColor('grey3')} />
              </div>
              <Typography component='h2' variant='h2'>
                {handleDialogTitle()}
              </Typography>
            </div>
          </DialogTitle>
          <DialogContent className={classes.formDialogContent}>
            <Typography>
              {handleDialogContent()}
            </Typography>
            <FormSection title={exportPath.label}
              legend={translate('common:editors.shared.fieldsRequired')}>
              <div className={classes.formTemplate}>
                <ChipTextField name={EmailExportFields.EmailAddress}
                  onUpdateInput={onUpdateInput}
                  onChange={handleFieldChange}
                  onBeforeAdd={onBeforeAdd}
                  blurBehavior={'add'}
                  fieldLabel={editors.label}
                  placeholder={editors.placeholder}
                  hasError={formState[EmailExportFields.EmailAddress]?.error || maxLengthError}
                  errorText={maxLengthError ? exportPath.maxInputLengthError : editors.error} />
              </div>
            </FormSection>
          </DialogContent>
          <DialogActions className={classes.formActions}>
            {onCloseDialog && <CancelButton onCancel={onCloseDialog} />}
            {onSubmit && (
              <div className={classes.primaryButton}>
                <SaveButton onSave={onSubmit}
                  label={exportPath.done}
                  loading={loading}
                  disabled={disable} />
              </div>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default React.memo(EmailExportRequestDialog);
